import { useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { fetchCurrentUser } from '../redux/authSlice';

const BASE_URL = process.env.REACT_APP_BASE_API_URL;
const REFRESH_BUFFER_MS = 60_000; // refresh 60s before expiry
const IDLE_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

export function useSessionManager({ expiresInSeconds, onExpired }) {
  const dispatch = useDispatch();
  const expireTimer = useRef(null);   // fires onExpired if no activity
  const refreshTimer = useRef(null);  // fires refresh if user is active
  const isActive = useRef(false);     // did user do anything since last refresh?

  const clearTimers = useCallback(() => {
    clearTimeout(expireTimer.current);
    clearTimeout(refreshTimer.current);
  }, []);

  const scheduleTimers = useCallback((ttlSeconds) => {
    if (!ttlSeconds || ttlSeconds <= 0) return;
    clearTimers();

    const ttlMs = ttlSeconds * 1000;

    // Hard expiry timer — fires regardless of activity
    expireTimer.current = setTimeout(() => {
      onExpired();
    }, ttlMs);

    // Refresh timer — fires 60s before expiry, but only if user was active
    const refreshDelay = Math.max(ttlMs - REFRESH_BUFFER_MS, 0);
    refreshTimer.current = setTimeout(async () => {
      if (isActive.current) {
        isActive.current = false; // reset for next cycle
        try {
          await fetch(`${BASE_URL}/mvapi-ui/auth/refresh`, {
            method: 'POST',
            credentials: 'include',
          });
          // Re-fetch /me to get new expiresIn, which will re-run this effect
          dispatch(fetchCurrentUser());
        } catch {
          onExpired();
        }
      }
      // If not active, do nothing — expireTimer will fire naturally
    }, refreshDelay);
  }, [clearTimers, dispatch, onExpired]);

  // Track user activity
  useEffect(() => {
    const markActive = () => { isActive.current = true; };
    IDLE_EVENTS.forEach(e => window.addEventListener(e, markActive, { passive: true }));
    return () => IDLE_EVENTS.forEach(e => window.removeEventListener(e, markActive));
  }, []);

  // Start/restart timers when expiresIn changes (i.e. after each refresh)
  useEffect(() => {
    if (expiresInSeconds) scheduleTimers(expiresInSeconds);
    return clearTimers;
  }, [expiresInSeconds, scheduleTimers, clearTimers]);
}